import { useState, useEffect } from "react";
import { landingSectionOneApi, LandingSectionOne, productSectionApi, ProductSection, intelligentDesignSectionApi, IntelligentDesignSection, featuresSectionApi, FeaturesSection, testimonialsSectionApi, TestimonialsSection, whoSectionApi, WhoSection, faqSectionApi, FaqSection, pricingSectionApi, PricingSection, waitingSectionApi, WhatAreYouWaitingForSection, footerApi, Footer, adminAuthApi, getAdminToken } from "@/services/api";

const defaultContent: LandingSectionOne = {
  title: "CLEANSE, DETOX AND CRUSH YOUR GOALS EVERY DAY. NATURALLY!",
  subtitle: "Take charge of your health and get the motivation you need to become the best version of yourself with nothing more than small, natural steps (and a few new ingredients).",
  mainImage: "https://images.unsplash.com/photo-1506744038136-46273834b3fb",
  description: "START YOUR JOURNEY TODAY",
};

const defaultProductSection: ProductSection = {
  sectionTitle: "DON'T MISS OUR BESTSELLING PRODUCTS",
  sectionSubtitle: "There's much more for you to discover!",
  products: [
    {
      badge: "POPULAR",
      img: "https://img.icons8.com/fluency/120/task-planning.png",
      title: "AI Scheduling Suite",
      rating: 5,
      reviews: 124,
      description: "Automate appointments, reminders, and reduce no-shows with our smart AI.",
      price: "Tk 1,500/mo"
    },
    {
      badge: "ESSENTIAL",
      img: "https://img.icons8.com/fluency/120/medical-record.png",
      title: "EMR & Patient Records",
      rating: 5,
      reviews: 210,
      description: "Securely manage all your patient charts, history, and sensitive data in one place.",
      price: "Tk 2,000/mo"
    },
    {
      badge: "GROWTH",
      img: "https://img.icons8.com/fluency/120/online-support.png",
      title: "Telemedicine Platform",
      rating: 4,
      reviews: 180,
      description: "Engage with patients remotely with our integrated, secure video consultation platform.",
      price: "Tk 2,500/mo"
    },
    {
      badge: "NEW",
      img: "https://img.icons8.com/fluency/120/receipt.png",
      title: "Billing & Invoicing",
      rating: 4,
      reviews: 98,
      description: "Streamline your financial workflow with automated billing and insurance processing.",
      price: "Tk 1,200/mo"
    },
    {
      badge: "SUITE",
      img: "https://img.icons8.com/fluency/120/clinic.png",
      title: "Full Practice Suite",
      rating: 5,
      reviews: 350,
      description: "The complete, all-in-one solution for managing and growing your modern clinic.",
      price: "Tk 5,500/mo"
    },
  ]
};

const defaultIntelligentDesignSection: IntelligentDesignSection = {
  heading: "INTELLIGENT BY DESIGN — BUILT FOR THE FUTURE OF HEALTHCARE",
  description: "We believe that modern healthcare deserves modern tools. Doctor System was built from the ground up with a focus on intuitive design, robust security, and seamless workflow integration. Our platform is crafted to empower healthcare professionals, automating the mundane so you can focus on what truly matters: your patients. Every feature is thoughtfully designed to be both powerful and easy to use, ensuring your practice runs more efficiently and effectively.",
  techLogos: [
    { text: "HIPAA COMPLIANT" },
    { text: "GDPR READY" },
    { text: "ISO 27001 CERTIFIED" }
  ],
  imageUrl: "https://nmc.ae/_next/image?url=https%3A%2F%2Fstatic-cdn.nmc.ae%2Fstrapi%2FDietetics_and_Nutrition_Getty_Images_690750224_706x500px_92aead0cbf.jpg&w=3840&q=75"
};

const defaultFeaturesSection: FeaturesSection = {
  heading: "NOT A DIET, JUST SMART HEALTHCARE",
  subtitle: "Delivered in Features That Fit Perfectly Around Your Practice.",
  features: [
    {
      iconUrl: "https://img.icons8.com/fluency/96/doctor-male.png",
      title: "AI Appointment Scheduling",
      description: "Automate bookings, reminders, and reduce no-shows with our smart AI-powered scheduler."
    },
    {
      iconUrl: "https://img.icons8.com/fluency/96/medical-history.png",
      title: "Complete Patient Records",
      description: "Access and update patient history, notes, and files securely from anywhere, anytime."
    },
    {
      iconUrl: "https://img.icons8.com/fluency/96/online-support.png",
      title: "Telemedicine & Video Calls",
      description: "Offer virtual consultations and follow-ups with secure, integrated video calls."
    },
    {
      iconUrl: "https://img.icons8.com/fluency/96/financial-growth-analysis.png",
      title: "Practice Analytics",
      description: "Track revenue, patient flow, and key metrics to grow your practice efficiently."
    },
    {
      iconUrl: "https://img.icons8.com/fluency/96/lock-2.png",
      title: "HIPAA-Grade Security",
      description: "Your data is protected with industry-leading encryption and compliance standards."
    },
    {
      iconUrl: "https://img.icons8.com/fluency/96/online-payment-.png",
      title: "Automated Billing",
      description: "Streamline payments, insurance claims, and invoicing with ease."
    }
  ],
  ctaButton: "START YOUR JOURNEY TODAY 🍃",
  ratingLabel: "OVER 1 MILLION USERS WORLDWIDE"
};

const defaultTestimonialsSection: TestimonialsSection = {
  heading: "IN THE EVER-GROWING COMMUNITY OF OVER 1 MILLION CLIENTS WHO LOVE THE BENEFITS OF DOCTOR SYSTEM!",
  ratingLabel: "OVER 1 MILLION CLIENTS WORLDWIDE",
  testimonials: [
    {
      photo: "https://randomuser.me/api/portraits/women/44.jpg",
      name: "Erin",
      role: "Clinic Owner",
      title: "WORKS AND TASTES GREAT!",
      quote: "If you love smart healthcare and want to save time, this is really effective. I tried Doctor System and I must say I'm lovin' how it streamlines my clinic! I use it every day and it's a game changer! Will definitely subscribe again :)"
    },
    {
      photo: "https://randomuser.me/api/portraits/men/32.jpg",
      name: "Dr. Alex",
      role: "Family Physician",
      title: "INCREDIBLE SUPPORT",
      quote: "The support team is amazing and the features are exactly what my practice needed. Highly recommended for any clinic!"
    },
    {
      photo: "https://randomuser.me/api/portraits/women/65.jpg",
      name: "Sarah",
      role: "Practice Manager",
      title: "SAVED US HOURS EVERY WEEK",
      quote: "Doctor System helped us automate so many tasks. Our staff and patients are happier, and we save hours every week!"
    }
  ]
};

const defaultWhoSection: WhoSection = {
  heading: "COMMITTED TO PROVIDING ONLY THE HIGHEST QUALITY",
  subtitle: "Our platform is developed by experts to satisfy your needs and help your clinic thrive, every time!",
  items: [
    {
      iconUrl: "https://img.icons8.com/color/96/guarantee.png",
      title: "30 DAYS MONEY BACK GUARANTEE",
      description: "We believe in our platform so much that we're willing to take all the risk for you! If for any reason at all, you're not satisfied, just email us and we'll refund you once we receive your request."
    },
    {
      iconUrl: "https://img.icons8.com/color/96/organic-food.png",
      title: "ALL-IN-ONE SOLUTION",
      description: "Every feature in Doctor System is designed to be intuitive, secure, and to help you run your practice more efficiently. You can rest assured your data is safe and your workflow is smooth!"
    },
    {
      iconUrl: "https://img.icons8.com/color/96/great-britain.png",
      title: "PROUDLY MADE FOR CLINICS",
      description: "Doctor System is built for clinics and healthcare professionals, with world-class quality, compliance, and support. Our team is always here to help you succeed!"
    }
  ],
  ctaButton: "START YOUR JOURNEY TODAY 🍃",
  ratingLabel: "OVER 1 MILLION CLIENTS WORLDWIDE"
};

const defaultFaqSection: FaqSection = {
  heading: "GOT ANY QUESTIONS? WE'VE GOT THE ANSWERS!",
  subtitle: "Find answers to the most common questions about Doctor System below.",
  faqs: [
    {
      question: "Will Doctor System help me grow my clinic?",
      answer: "Absolutely! Our platform is designed to streamline your workflow, improve patient satisfaction, and help you grow your business."
    },
    {
      question: "Is my data secure?",
      answer: "Yes, we use HIPAA-grade security and encryption to keep your data safe and private at all times."
    },
    {
      question: "Can I use Doctor System on mobile?",
      answer: "Of course! Our platform is fully responsive and works on any device, anywhere."
    },
    {
      question: "How do I get support?",
      answer: "You can reach our support team 24/7 via chat, email, or phone. We're always here to help!"
    },
    {
      question: "Is there a free trial?",
      answer: "Yes! You can try Doctor System free for 30 days, no credit card required."
    }
  ],
  ratingLabel: "Rated Excellent by 1,000+ Clinics"
};

const defaultPricingSection: PricingSection = {
  heading: "Choose Your Plan",
  subtitle: "Pick the plan that's right for you. Get started for free, or unlock powerful pro features.",
  plans: [
    {
      name: "Starter",
      price: "TK0",
      description: "For individuals and small teams getting started with our system.",
      features: [
        "Core Features",
        "10 Client Profiles",
        "Basic Analytics",
        "24/7 Support"
      ],
      buttonText: "Get Started",
      isPopular: false,
      popularBadge: "MOST POPULAR"
    },
    {
      name: "Pro",
      price: "TK5,500",
      description: "For professionals who need advanced tools and full capabilities.",
      features: [
        "All Core Features",
        "Unlimited Clients",
        "Advanced Analytics & Reports",
        "24/7 Priority Support"
      ],
      buttonText: "Choose Pro",
      isPopular: true,
      popularBadge: "MOST POPULAR"
    },
    {
      name: "Enterprise",
      price: "TK20,000",
      description: "For large organizations with custom needs and dedicated support.",
      features: [
        "Everything in Pro",
        "Custom Integrations",
        "Dedicated Account Manager",
        "On-Premise Options"
      ],
      buttonText: "Contact Us",
      isPopular: false,
      popularBadge: "MOST POPULAR"
    }
  ]
};

const defaultWaitingSection: WhatAreYouWaitingForSection = {
  headline: "WHAT ARE YOU WAITING FOR?",
  description: "It's time to experience the Doctor System difference for yourself. Get the energy, focus, and results you need to boost your clinic's performance and achieve the success you've always dreamed of!",
  ctaText: "START YOUR JOURNEY TODAY",
  ratingLabel: "OVER 1 MILLION CLIENTS WORLDWIDE"
};

const defaultFooter: Footer = {
  brand: {
    logoText: 'DOCTOR SYSTEM',
    disclaimer: 'Disclaimer: Doctor System is not a substitute for medical advice. Always consult a healthcare professional for medical concerns.',
    socialLinks: [
      { platform: 'Facebook', url: '#', icon: 'https://img.icons8.com/color/32/facebook-new.png' },
      { platform: 'Twitter', url: '#', icon: 'https://img.icons8.com/color/32/twitter--v1.png' },
      { platform: 'Instagram', url: '#', icon: 'https://img.icons8.com/color/32/instagram-new.png' }
    ]
  },
  quickLinks: [
    { text: 'Shop', url: '#' },
    { text: 'Cart', url: '#' },
    { text: 'Privacy Policy', url: '#' },
    { text: 'Terms & Conditions', url: '#' },
    { text: 'Blog', url: '#' },
    { text: 'Trade & Wholesale', url: '#' }
  ],
  customerService: [
    { text: 'Contact Us', url: '#' },
    { text: 'Delivery', url: '#' },
    { text: 'Refund Policy', url: '#' },
    { text: 'My Account', url: '#' }
  ],
  paymentMethods: [
    { name: 'PayPal', icon: 'https://img.icons8.com/color/32/paypal.png' },
    { name: 'Visa', icon: 'https://img.icons8.com/color/32/visa.png' },
    { name: 'Mastercard', icon: 'https://img.icons8.com/color/32/mastercard-logo.png' },
    { name: 'Discover', icon: 'https://img.icons8.com/color/32/discover.png' },
    { name: 'Amex', icon: 'https://img.icons8.com/color/32/amex.png' }
  ]
};

export default function WebsiteAdmin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [content, setContent] = useState<LandingSectionOne>(defaultContent);
  const [productSection, setProductSection] = useState<ProductSection>(defaultProductSection);
  const [intelligentDesignSection, setIntelligentDesignSection] = useState<IntelligentDesignSection>(defaultIntelligentDesignSection);
  const [featuresSection, setFeaturesSection] = useState<FeaturesSection>(defaultFeaturesSection);
  const [testimonialsSection, setTestimonialsSection] = useState<TestimonialsSection>(defaultTestimonialsSection);
  const [whoSection, setWhoSection] = useState<WhoSection>(defaultWhoSection);
  const [faqSection, setFaqSection] = useState<FaqSection>(defaultFaqSection);
  const [pricingSection, setPricingSection] = useState<PricingSection>(defaultPricingSection);
  const [waitingSection, setWaitingSection] = useState<WhatAreYouWaitingForSection>(defaultWaitingSection);
  const [footer, setFooter] = useState<Footer>(defaultFooter);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'hero' | 'products' | 'intelligent-design' | 'features' | 'testimonials' | 'who' | 'faq' | 'pricing' | 'waiting' | 'footer'>('hero');
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);

  const [submittingLogin, setSubmittingLogin] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingLogin) return;
    setSubmittingLogin(true);
    setLoginError("");
    try {
      await adminAuthApi.login({ email, password });
      setIsAuthenticated(true);
      setPassword("");
    } catch (err: any) {
      setLoginError(err?.message || "Invalid email or password");
    } finally {
      setSubmittingLogin(false);
    }
  };

  const handleLogout = () => {
    adminAuthApi.logout();
    setIsAuthenticated(false);
    setEmail("");
    setPassword("");
  };

  useEffect(() => {
    const verify = async () => {
      if (!getAdminToken()) return;
      try {
        await adminAuthApi.me();
        setIsAuthenticated(true);
      } catch {
        adminAuthApi.logout();
      }
    };
    verify();
  }, []);

  useEffect(() => {
    Promise.all([
      landingSectionOneApi.get().catch(() => defaultContent),
      productSectionApi.get().catch(() => defaultProductSection),
      intelligentDesignSectionApi.get().catch(() => defaultIntelligentDesignSection),
      featuresSectionApi.get().catch(() => defaultFeaturesSection),
      testimonialsSectionApi.get().catch(() => defaultTestimonialsSection),
      whoSectionApi.get().catch(() => defaultWhoSection),
      faqSectionApi.get().catch(() => defaultFaqSection),
      pricingSectionApi.get().catch(() => defaultPricingSection),
      waitingSectionApi.get().catch(() => defaultWaitingSection),
      footerApi.get().catch(() => defaultFooter)
    ])
      .then(([heroData, productData, intelligentDesignData, featuresData, testimonialsData, whoData, faqData, pricingData, waitingData, footerData]) => {
        setContent(heroData);
        setProductSection(productData);
        setIntelligentDesignSection(intelligentDesignData);
        setFeaturesSection(featuresData);
        setTestimonialsSection(testimonialsData);
        setWhoSection(whoData);
        setFaqSection(faqData);
        setPricingSection(pricingData);
        setWaitingSection(waitingData);
        setFooter(footerData);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setContent({ ...content, [e.target.name]: e.target.value });
  };

  const handleProductSectionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setProductSection({ ...productSection, [e.target.name]: e.target.value });
  };

  const handleProductChange = (index: number, field: string, value: string | number) => {
    const updatedProducts = [...productSection.products];
    updatedProducts[index] = { ...updatedProducts[index], [field]: value };
    setProductSection({ ...productSection, products: updatedProducts });
  };

  const handleAddProduct = () => {
    if (productSection.products.length >= 10) {
      alert("Maximum 10 products allowed!");
      return;
    }
    const newProduct = {
      badge: "NEW",
      img: "https://img.icons8.com/fluency/120/plus.png",
      title: "New Product",
      rating: 5,
      reviews: 0,
      description: "Product description",
      price: "Tk 0/mo"
    };
    setProductSection({
      ...productSection,
      products: [...productSection.products, newProduct]
    });
  };

  const handleDeleteProduct = (index: number) => {
    if (productSection.products.length <= 1) {
      alert("You must have at least one product!");
      return;
    }
    const updatedProducts = productSection.products.filter((_, i) => i !== index);
    setProductSection({ ...productSection, products: updatedProducts });
    setDeleteConfirmIndex(null);
  };

  const confirmDelete = () => {
    if (deleteConfirmIndex !== null) {
      handleDeleteProduct(deleteConfirmIndex);
    }
  };

  const moveProductUp = (index: number) => {
    if (index === 0) return;
    const updatedProducts = [...productSection.products];
    [updatedProducts[index], updatedProducts[index - 1]] = [updatedProducts[index - 1], updatedProducts[index]];
    setProductSection({ ...productSection, products: updatedProducts });
  };

  const moveProductDown = (index: number) => {
    if (index === productSection.products.length - 1) return;
    const updatedProducts = [...productSection.products];
    [updatedProducts[index], updatedProducts[index + 1]] = [updatedProducts[index + 1], updatedProducts[index]];
    setProductSection({ ...productSection, products: updatedProducts });
  };

  const duplicateProduct = (index: number) => {
    const productToDuplicate = productSection.products[index];
    const duplicatedProduct = {
      ...productToDuplicate,
      title: `${productToDuplicate.title} (Copy)`,
      badge: "NEW"
    };
    const updatedProducts = [...productSection.products];
    updatedProducts.splice(index + 1, 0, duplicatedProduct);
    setProductSection({ ...productSection, products: updatedProducts });
  };

  const handleSave = async () => {
    setStatus("Saving...");
    try {
      if (activeTab === 'hero') {
        await landingSectionOneApi.update(content);
      } else if (activeTab === 'products') {
        await productSectionApi.update(productSection);
      } else if (activeTab === 'intelligent-design') {
        await intelligentDesignSectionApi.update(intelligentDesignSection);
      } else if (activeTab === 'features') {
        await featuresSectionApi.update(featuresSection);
      } else if (activeTab === 'testimonials') {
        await testimonialsSectionApi.update(testimonialsSection);
      } else if (activeTab === 'who') {
        await whoSectionApi.update(whoSection);
      } else if (activeTab === 'faq') {
        await faqSectionApi.update(faqSection);
      } else if (activeTab === 'pricing') {
        await pricingSectionApi.update(pricingSection);
      } else if (activeTab === 'waiting') {
        await waitingSectionApi.update(waitingSection);
      } else if (activeTab === 'footer') {
        await footerApi.update(footer);
      }
      setStatus("Saved!");
    } catch (err) {
      setStatus("Error saving");
    }
    setTimeout(() => setStatus(""), 1500);
  };

  // If not authenticated, show login form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg border p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Website Admin</h1>
              <p className="text-gray-600">Please log in to access the admin panel</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Enter your email"
                  required
                  autoComplete="email"
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                />
              </div>
              
              {loginError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{loginError}</p>
                </div>
              )}
              
              <button
                type="submit"
                disabled={submittingLogin}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submittingLogin ? "Signing in..." : "Sign In"}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="flex items-center space-x-3">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <span className="text-gray-600">Loading website content...</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Website Admin</h1>
              <p className="text-sm text-gray-600 mt-1">Control your landing page content</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                onClick={handleSave}
              >
                Save Changes
              </button>
              <button
                onClick={handleLogout}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                Logout
              </button>
              {status && (
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  status === "Saved!" ? "bg-green-100 text-green-800" : 
                  status === "Saving..." ? "bg-blue-100 text-blue-800" : 
                  "bg-red-100 text-red-800"
                }`}>
                  {status}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'hero' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('hero')}
            >
              Hero Section
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'products' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('products')}
            >
              Product Section
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'intelligent-design' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('intelligent-design')}
            >
              Intelligent Design
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'features' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('features')}
            >
              Features
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'testimonials' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('testimonials')}
            >
              Testimonials
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'who' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('who')}
            >
              Who Section
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'faq' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('faq')}
            >
              FAQ
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'pricing' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('pricing')}
            >
              Pricing
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'waiting' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('waiting')}
            >
              What Are You Waiting For
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'footer' 
                  ? 'border-blue-500 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('footer')}
            >
              Footer
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'hero' ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Content Editor */}
            <div className="xl:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Hero Section Content</h2>
                  <p className="text-sm text-gray-600 mt-1">Edit the main content of your landing page's first section</p>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Main Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Main Title
                    </label>
                    <textarea
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                      name="title"
                      value={content.title}
                      onChange={handleChange}
                      placeholder="Enter the main hero title..."
                      rows={3}
                    />
                    <p className="text-xs text-gray-500 mt-1">This is the large heading that appears at the top of the page</p>
                  </div>
                  
                  {/* Subtitle */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subtitle
                    </label>
                    <textarea
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                      name="subtitle"
                      value={content.subtitle}
                      onChange={handleChange}
                      placeholder="Enter the subtitle description..."
                      rows={4}
                    />
                    <p className="text-xs text-gray-500 mt-1">This is the descriptive text below the main title</p>
                  </div>
                  
                  {/* CTA Button */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Call-to-Action Button
                    </label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      name="description"
                      value={content.description}
                      onChange={handleChange}
                      placeholder="Enter the call-to-action button text..."
                    />
                    <p className="text-xs text-gray-500 mt-1">This is the text on the main action button</p>
                  </div>
                  
                  {/* Background Image */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Background Image URL
                    </label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      name="mainImage"
                      value={content.mainImage}
                      onChange={handleChange}
                      placeholder="Enter the background image URL..."
                    />
                    <p className="text-xs text-gray-500 mt-1">URL for the hero section background image</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Preview & Tips */}
            <div className="space-y-6">
              {/* Preview */}
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Live Preview</h3>
                </div>
                <div className="p-6">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg p-6 border border-blue-200">
                    <div className="space-y-4">
                      <h2 className="text-lg font-bold text-gray-900 leading-tight">
                        {content.title || "Main Title"}
                      </h2>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {content.subtitle || "Subtitle description"}
                      </p>
                      <button className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                        {content.description || "CTA Button"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Tips */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Content Tips
                </h4>
                <ul className="text-sm text-blue-800 space-y-2">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Keep the main title short and impactful
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    The subtitle should explain your value proposition
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    CTA button should be action-oriented
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Use high-quality images for the background
                  </li>
                </ul>
              </div>
            </div>
          </div>
        ) : activeTab === 'products' ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Product Section Editor */}
            <div className="xl:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Product Section Content</h2>
                  <p className="text-sm text-gray-600 mt-1">Edit the product showcase section of your landing page</p>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Section Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Section Title
                    </label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      name="sectionTitle"
                      value={productSection.sectionTitle}
                      onChange={handleProductSectionChange}
                      placeholder="Enter the section title..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Main heading for the product section</p>
                  </div>
                  
                  {/* Section Subtitle */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Section Subtitle
                    </label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      name="sectionSubtitle"
                      value={productSection.sectionSubtitle}
                      onChange={handleProductSectionChange}
                      placeholder="Enter the section subtitle..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Subtitle text below the main heading</p>
                  </div>
                  
                  {/* Products */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Products ({productSection.products.length})
                      </label>
                      <button
                        onClick={handleAddProduct}
                        disabled={productSection.products.length >= 10}
                        className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center ${
                          productSection.products.length >= 10
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add Product {productSection.products.length >= 10 && '(Max Reached)'}
                      </button>
                    </div>
                    <div className="space-y-4">
                      {productSection.products.map((product, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900">Product {index + 1}</h4>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-500">#{index + 1}</span>
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => moveProductUp(index)}
                                  disabled={index === 0}
                                  className={`p-1 transition-colors ${
                                    index === 0 
                                      ? 'text-gray-300 cursor-not-allowed' 
                                      : 'text-gray-600 hover:text-gray-800'
                                  }`}
                                  title="Move Up"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => moveProductDown(index)}
                                  disabled={index === productSection.products.length - 1}
                                  className={`p-1 transition-colors ${
                                    index === productSection.products.length - 1 
                                      ? 'text-gray-300 cursor-not-allowed' 
                                      : 'text-gray-600 hover:text-gray-800'
                                  }`}
                                  title="Move Down"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                              </div>
                              <button
                                onClick={() => duplicateProduct(index)}
                                className="text-blue-600 hover:text-blue-800 transition-colors p-1"
                                title="Duplicate Product"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => setDeleteConfirmIndex(index)}
                                className="text-red-600 hover:text-red-800 transition-colors p-1"
                                title="Delete Product"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Badge</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={product.badge}
                                onChange={(e) => handleProductChange(index, 'badge', e.target.value)}
                                placeholder="e.g., POPULAR, NEW"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={product.title}
                                onChange={(e) => handleProductChange(index, 'title', e.target.value)}
                                placeholder="Product title"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Image URL</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={product.img}
                                onChange={(e) => handleProductChange(index, 'img', e.target.value)}
                                placeholder="Product image URL"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Price</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={product.price}
                                onChange={(e) => handleProductChange(index, 'price', e.target.value)}
                                placeholder="e.g., Tk 1,500/mo"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Rating (1-5)</label>
                              <input
                                type="number"
                                min="1"
                                max="5"
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={product.rating}
                                onChange={(e) => handleProductChange(index, 'rating', parseInt(e.target.value) || 1)}
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Reviews Count</label>
                              <input
                                type="number"
                                min="0"
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={product.reviews}
                                onChange={(e) => handleProductChange(index, 'reviews', parseInt(e.target.value) || 0)}
                              />
                            </div>
                          </div>
                          
                          <div className="mt-4">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                              value={product.description}
                              onChange={(e) => handleProductChange(index, 'description', e.target.value)}
                              placeholder="Product description"
                              rows={3}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Product Limits Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Product Management Tips:</p>
                        <ul className="space-y-1 text-xs">
                          <li>• Minimum 1 product required, maximum 10 products allowed</li>
                          <li>• Use the up/down arrows to reorder products</li>
                          <li>• Duplicate products to quickly create similar items</li>
                          <li>• Products are displayed in a carousel on the landing page</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Preview & Tips */}
            <div className="space-y-6">
              {/* Preview */}
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Section Preview</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    <h2 className="text-lg font-bold text-gray-900">
                      {productSection.sectionTitle || "Section Title"}
                    </h2>
                    <p className="text-sm text-gray-600">
                      {productSection.sectionSubtitle || "Section subtitle"}
                    </p>
                    
                    {/* Product Preview */}
                    <div className="space-y-3">
                      {productSection.products.slice(0, 2).map((product, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs bg-pink-100 text-pink-800 px-2 py-1 rounded">
                              {product.badge}
                            </span>
                            <span className="text-xs text-gray-500">{product.price}</span>
                          </div>
                          <h4 className="font-medium text-sm text-gray-900 mb-1">{product.title}</h4>
                          <p className="text-xs text-gray-600 line-clamp-2">{product.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Tips */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h4 className="font-semibold text-green-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Product Tips
                </h4>
                <ul className="text-sm text-green-800 space-y-2">
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">•</span>
                    Use clear, benefit-focused product titles
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">•</span>
                    Keep descriptions concise but compelling
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">•</span>
                    Use high-quality product images
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">•</span>
                    Set realistic ratings and review counts
                  </li>
                </ul>
              </div>
            </div>
          </div>
        ) : activeTab === 'intelligent-design' ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Intelligent Design Editor */}
            <div className="xl:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Intelligent Design Content</h2>
                  <p className="text-sm text-gray-600 mt-1">Edit the intelligent design section of your landing page</p>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Heading */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Heading
                    </label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      name="heading"
                      value={intelligentDesignSection.heading}
                      onChange={(e) => setIntelligentDesignSection({ ...intelligentDesignSection, heading: e.target.value })}
                      placeholder="Enter the heading..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Main heading for the intelligent design section</p>
                  </div>
                  
                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                      name="description"
                      value={intelligentDesignSection.description}
                      onChange={(e) => setIntelligentDesignSection({ ...intelligentDesignSection, description: e.target.value })}
                      placeholder="Enter the description..."
                      rows={4}
                    />
                    <p className="text-xs text-gray-500 mt-1">Description text for the intelligent design section</p>
                  </div>
                  
                  {/* Tech Logos */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tech Logos
                    </label>
                    <textarea
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                      name="techLogos"
                      value={intelligentDesignSection.techLogos.map(logo => logo.text).join('\n')}
                      onChange={(e) => setIntelligentDesignSection({
                        ...intelligentDesignSection,
                        techLogos: e.target.value.split('\n').map(text => ({ text }))
                      })}
                      placeholder="Enter tech logos, one per line..."
                      rows={4}
                    />
                    <p className="text-xs text-gray-500 mt-1">List of tech logos for the intelligent design section</p>
                  </div>
                  
                  {/* Image URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image URL
                    </label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      name="imageUrl"
                      value={intelligentDesignSection.imageUrl}
                      onChange={(e) => setIntelligentDesignSection({ ...intelligentDesignSection, imageUrl: e.target.value })}
                      placeholder="Enter the image URL..."
                    />
                    <p className="text-xs text-gray-500 mt-1">URL for the intelligent design section image</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Preview & Tips */}
            <div className="space-y-6">
              {/* Preview */}
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Section Preview</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    <h2 className="text-lg font-bold text-gray-900">
                      {intelligentDesignSection.heading || "Heading"}
                    </h2>
                    <p className="text-sm text-gray-600">
                      {intelligentDesignSection.description || "Description"}
                    </p>
                    
                    {/* Image Preview */}
                    <div className="h-64 overflow-hidden rounded-lg">
                      <img
                        src={intelligentDesignSection.imageUrl}
                        alt="Intelligent Design"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Tips */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Intelligent Design Tips
                </h4>
                <ul className="text-sm text-blue-800 space-y-2">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Use a clear and concise heading
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Explain the value proposition of the design
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Use high-quality images
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Keep the design simple and impactful
                  </li>
                </ul>
              </div>
            </div>
          </div>
        ) : activeTab === 'features' ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Features Section Editor */}
            <div className="xl:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Features Section Content</h2>
                  <p className="text-sm text-gray-600 mt-1">Edit the features section of your landing page</p>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Heading */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Heading
                    </label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      name="heading"
                      value={featuresSection.heading}
                      onChange={(e) => setFeaturesSection({ ...featuresSection, heading: e.target.value })}
                      placeholder="Enter the heading..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Main heading for the features section</p>
                  </div>
                  
                  {/* Subtitle */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subtitle
                    </label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      name="subtitle"
                      value={featuresSection.subtitle}
                      onChange={(e) => setFeaturesSection({ ...featuresSection, subtitle: e.target.value })}
                      placeholder="Enter the subtitle..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Subtitle text below the main heading</p>
                  </div>
                  
                  {/* Features */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Features ({featuresSection.features.length})
                      </label>
                      <button
                        onClick={() => setFeaturesSection({
                          ...featuresSection,
                          features: [...featuresSection.features, {
                            iconUrl: "",
                            title: "",
                            description: ""
                          }]
                        })}
                        className="px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add Feature
                      </button>
                    </div>
                    <div className="space-y-4">
                      {featuresSection.features.map((feature, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900">Feature {index + 1}</h4>
                            <button
                              onClick={() => setFeaturesSection({
                                ...featuresSection,
                                features: featuresSection.features.filter((_, i) => i !== index)
                              })}
                              className="text-red-600 hover:text-red-800 transition-colors p-1"
                              title="Delete Feature"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Icon URL</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={feature.iconUrl}
                                onChange={(e) => setFeaturesSection({
                                  ...featuresSection,
                                  features: featuresSection.features.map((f, i) =>
                                    i === index ? { ...f, iconUrl: e.target.value } : f
                                  )
                                })}
                                placeholder="Icon URL"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={feature.title}
                                onChange={(e) => setFeaturesSection({
                                  ...featuresSection,
                                  features: featuresSection.features.map((f, i) =>
                                    i === index ? { ...f, title: e.target.value } : f
                                  )
                                })}
                                placeholder="Feature title"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                              <textarea
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                value={feature.description}
                                onChange={(e) => setFeaturesSection({
                                  ...featuresSection,
                                  features: featuresSection.features.map((f, i) =>
                                    i === index ? { ...f, description: e.target.value } : f
                                  )
                                })}
                                placeholder="Feature description"
                                rows={3}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Preview & Tips */}
            <div className="space-y-6">
              {/* Preview */}
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Section Preview</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    <h2 className="text-lg font-bold text-gray-900">
                      {featuresSection.heading || "Heading"}
                    </h2>
                    <p className="text-sm text-gray-600">
                      {featuresSection.subtitle || "Subtitle"}
                    </p>
                    
                    {/* Feature Preview */}
                    <div className="space-y-3">
                      {featuresSection.features.map((feature, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="h-10 w-10 rounded-full overflow-hidden">
                              <img
                                src={feature.iconUrl}
                                alt={feature.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>
                          <h4 className="font-medium text-sm text-gray-900 mb-1">{feature.title}</h4>
                          <p className="text-xs text-gray-600 line-clamp-2">{feature.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Tips */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Features Tips
                </h4>
                <ul className="text-sm text-blue-800 space-y-2">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Use clear and concise titles
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Keep descriptions concise but compelling
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Use high-quality icons
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Keep the design simple and impactful
                  </li>
                </ul>
              </div>
            </div>
          </div>
        ) : activeTab === 'testimonials' ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Testimonials Section Editor */}
            <div className="xl:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Testimonials Section Content</h2>
                  <p className="text-sm text-gray-600 mt-1">Edit the testimonials section of your landing page</p>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Heading */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Heading
                    </label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      name="heading"
                      value={testimonialsSection.heading}
                      onChange={(e) => setTestimonialsSection({ ...testimonialsSection, heading: e.target.value })}
                      placeholder="Enter the heading..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Main heading for the testimonials section</p>
                  </div>
                  
                  {/* Rating Label */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rating Label
                    </label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      name="ratingLabel"
                      value={testimonialsSection.ratingLabel}
                      onChange={(e) => setTestimonialsSection({ ...testimonialsSection, ratingLabel: e.target.value })}
                      placeholder="Enter the rating label..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Label for the rating in testimonials</p>
                  </div>
                  
                  {/* Testimonials */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Testimonials ({testimonialsSection.testimonials.length})
                      </label>
                      <button
                        onClick={() => setTestimonialsSection({
                          ...testimonialsSection,
                          testimonials: [...testimonialsSection.testimonials, {
                            photo: "",
                            name: "",
                            role: "",
                            title: "",
                            quote: ""
                          }]
                        })}
                        className="px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add Testimonial
                      </button>
                    </div>
                    <div className="space-y-4">
                      {testimonialsSection.testimonials.map((testimonial, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900">Testimonial {index + 1}</h4>
                            <button
                              onClick={() => setTestimonialsSection({
                                ...testimonialsSection,
                                testimonials: testimonialsSection.testimonials.filter((_, i) => i !== index)
                              })}
                              className="text-red-600 hover:text-red-800 transition-colors p-1"
                              title="Delete Testimonial"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Photo URL</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={testimonial.photo}
                                onChange={(e) => setTestimonialsSection({
                                  ...testimonialsSection,
                                  testimonials: testimonialsSection.testimonials.map((t, i) =>
                                    i === index ? { ...t, photo: e.target.value } : t
                                  )
                                })}
                                placeholder="Photo URL"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={testimonial.name}
                                onChange={(e) => setTestimonialsSection({
                                  ...testimonialsSection,
                                  testimonials: testimonialsSection.testimonials.map((t, i) =>
                                    i === index ? { ...t, name: e.target.value } : t
                                  )
                                })}
                                placeholder="Name"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={testimonial.role}
                                onChange={(e) => setTestimonialsSection({
                                  ...testimonialsSection,
                                  testimonials: testimonialsSection.testimonials.map((t, i) =>
                                    i === index ? { ...t, role: e.target.value } : t
                                  )
                                })}
                                placeholder="Role"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={testimonial.title}
                                onChange={(e) => setTestimonialsSection({
                                  ...testimonialsSection,
                                  testimonials: testimonialsSection.testimonials.map((t, i) =>
                                    i === index ? { ...t, title: e.target.value } : t
                                  )
                                })}
                                placeholder="Title"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Quote</label>
                              <textarea
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                value={testimonial.quote}
                                onChange={(e) => setTestimonialsSection({
                                  ...testimonialsSection,
                                  testimonials: testimonialsSection.testimonials.map((t, i) =>
                                    i === index ? { ...t, quote: e.target.value } : t
                                  )
                                })}
                                placeholder="Quote"
                                rows={3}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Preview & Tips */}
            <div className="space-y-6">
              {/* Preview */}
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Section Preview</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    <h2 className="text-lg font-bold text-gray-900">
                      {testimonialsSection.heading || "Heading"}
                    </h2>
                    <p className="text-sm text-gray-600">
                      {testimonialsSection.ratingLabel || "Rating Label"}
                    </p>
                    
                    {/* Testimonial Preview */}
                    <div className="space-y-3">
                      {testimonialsSection.testimonials.map((testimonial, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="h-10 w-10 rounded-full overflow-hidden">
                              <img
                                src={testimonial.photo}
                                alt={testimonial.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>
                          <h4 className="font-medium text-sm text-gray-900 mb-1">{testimonial.name}</h4>
                          <p className="text-xs text-gray-600 line-clamp-2">{testimonial.quote}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Tips */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Testimonials Tips
                </h4>
                <ul className="text-sm text-blue-800 space-y-2">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Use clear and concise titles
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Keep quotes concise but compelling
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Use high-quality photos
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Keep the design simple and impactful
                  </li>
                </ul>
              </div>
            </div>
          </div>
                 ) : activeTab === 'who' ? (
           <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
             {/* Who Section Editor */}
            <div className="xl:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Who Section Content</h2>
                  <p className="text-sm text-gray-600 mt-1">Edit the who section of your landing page</p>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Heading */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Heading
                    </label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      name="heading"
                      value={whoSection.heading}
                      onChange={(e) => setWhoSection({ ...whoSection, heading: e.target.value })}
                      placeholder="Enter the heading..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Main heading for the who section</p>
                  </div>
                  
                  {/* Subtitle */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subtitle
                    </label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      name="subtitle"
                      value={whoSection.subtitle}
                      onChange={(e) => setWhoSection({ ...whoSection, subtitle: e.target.value })}
                      placeholder="Enter the subtitle..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Subtitle text below the main heading</p>
                  </div>
                  
                  {/* Items */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Items ({whoSection.items.length})
                      </label>
                      <button
                        onClick={() => setWhoSection({
                          ...whoSection,
                          items: [...whoSection.items, {
                            iconUrl: "",
                            title: "",
                            description: ""
                          }]
                        })}
                        className="px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add Item
                      </button>
                    </div>
                    <div className="space-y-4">
                      {whoSection.items.map((item, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900">Item {index + 1}</h4>
                            <button
                              onClick={() => setWhoSection({
                                ...whoSection,
                                items: whoSection.items.filter((_, i) => i !== index)
                              })}
                              className="text-red-600 hover:text-red-800 transition-colors p-1"
                              title="Delete Item"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Icon URL</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={item.iconUrl}
                                onChange={(e) => setWhoSection({
                                  ...whoSection,
                                  items: whoSection.items.map((item, idx) =>
                                    idx === index ? { ...item, iconUrl: e.target.value } : item
                                  )
                                })}
                                placeholder="Icon URL"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={item.title}
                                onChange={(e) => setWhoSection({
                                  ...whoSection,
                                  items: whoSection.items.map((item, idx) =>
                                    idx === index ? { ...item, title: e.target.value } : item
                                  )
                                })}
                                placeholder="Item title"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                              <textarea
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                value={item.description}
                                onChange={(e) => setWhoSection({
                                  ...whoSection,
                                  items: whoSection.items.map((item, idx) =>
                                    idx === index ? { ...item, description: e.target.value } : item
                                  )
                                })}
                                placeholder="Item description"
                                rows={3}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Preview & Tips */}
            <div className="space-y-6">
              {/* Preview */}
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Section Preview</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    <h2 className="text-lg font-bold text-gray-900">
                      {whoSection.heading || "Heading"}
                    </h2>
                    <p className="text-sm text-gray-600">
                      {whoSection.subtitle || "Subtitle"}
                    </p>
                    
                    {/* Item Preview */}
                    <div className="space-y-3">
                      {whoSection.items.map((item, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="h-10 w-10 rounded-full overflow-hidden">
                              <img
                                src={item.iconUrl}
                                alt={item.title}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>
                          <h4 className="font-medium text-sm text-gray-900 mb-1">{item.title}</h4>
                          <p className="text-xs text-gray-600 line-clamp-2">{item.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Tips */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Who Section Tips
                </h4>
                <ul className="text-sm text-blue-800 space-y-2">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Use clear and concise titles
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Keep descriptions concise but compelling
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Use high-quality icons
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">•</span>
                    Keep the design simple and impactful
                  </li>
                </ul>
              </div>
            </div>
          </div>
        ) : activeTab === 'faq' ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* FAQ Section Editor */}
            <div className="xl:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">FAQ Section Content</h2>
                  <p className="text-sm text-gray-600 mt-1">Edit the FAQ section of your landing page</p>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Heading */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Heading</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={faqSection.heading}
                      onChange={(e) => setFaqSection({ ...faqSection, heading: e.target.value })}
                      placeholder="Enter the heading..."
                    />
                  </div>
                  
                  {/* Subtitle */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={faqSection.subtitle}
                      onChange={(e) => setFaqSection({ ...faqSection, subtitle: e.target.value })}
                      placeholder="Enter the subtitle..."
                    />
                  </div>
                  
                  {/* Rating Label */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rating Label</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={faqSection.ratingLabel}
                      onChange={(e) => setFaqSection({ ...faqSection, ratingLabel: e.target.value })}
                      placeholder="Enter the rating label..."
                    />
                  </div>
                  
                  {/* FAQs */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-sm font-medium text-gray-700">FAQs ({faqSection.faqs.length})</label>
                      <button
                        onClick={() => setFaqSection({
                          ...faqSection,
                          faqs: [...faqSection.faqs, { question: "", answer: "" }]
                        })}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        Add FAQ
                      </button>
                    </div>
                    <div className="space-y-4">
                      {faqSection.faqs.map((faq, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900">FAQ {index + 1}</h4>
                            <button
                              onClick={() => setFaqSection({
                                ...faqSection,
                                faqs: faqSection.faqs.filter((_, i) => i !== index)
                              })}
                              className="text-red-600 hover:text-red-800 transition-colors p-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          
                          <div className="space-y-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Question</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={faq.question}
                                onChange={(e) => setFaqSection({
                                  ...faqSection,
                                  faqs: faqSection.faqs.map((f, idx) =>
                                    idx === index ? { ...f, question: e.target.value } : f
                                  )
                                })}
                                placeholder="Enter the question..."
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Answer</label>
                              <textarea
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                value={faq.answer}
                                onChange={(e) => setFaqSection({
                                  ...faqSection,
                                  faqs: faqSection.faqs.map((f, idx) =>
                                    idx === index ? { ...f, answer: e.target.value } : f
                                  )
                                })}
                                placeholder="Enter the answer..."
                                rows={3}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Preview */}
            <div className="bg-white rounded-xl shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Section Preview</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <h2 className="text-lg font-bold text-gray-900">{faqSection.heading || "Heading"}</h2>
                  <p className="text-sm text-gray-600">{faqSection.subtitle || "Subtitle"}</p>
                  
                  <div className="space-y-3">
                    {faqSection.faqs.map((faq, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3">
                        <h4 className="font-medium text-sm text-gray-900 mb-1">{faq.question}</h4>
                        <p className="text-xs text-gray-600 line-clamp-2">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                  
                  <p className="text-xs text-gray-500">{faqSection.ratingLabel || "Rating Label"}</p>
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'pricing' ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Pricing Section Editor */}
            <div className="xl:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Pricing Section Content</h2>
                  <p className="text-sm text-gray-600 mt-1">Edit the pricing section of your landing page</p>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Heading */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Heading</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={pricingSection.heading}
                      onChange={(e) => setPricingSection({ ...pricingSection, heading: e.target.value })}
                      placeholder="Enter the heading..."
                    />
                  </div>
                  
                  {/* Subtitle */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={pricingSection.subtitle}
                      onChange={(e) => setPricingSection({ ...pricingSection, subtitle: e.target.value })}
                      placeholder="Enter the subtitle..."
                    />
                  </div>
                  
                  {/* Pricing Plans */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-sm font-medium text-gray-700">Pricing Plans ({pricingSection.plans.length})</label>
                      <button
                        onClick={() => setPricingSection({
                          ...pricingSection,
                          plans: [...pricingSection.plans, {
                            name: "New Plan",
                            price: "Tk 0",
                            description: "Plan description",
                            features: ["Feature 1", "Feature 2"],
                            buttonText: "Get Started",
                            isPopular: false,
                            popularBadge: "MOST POPULAR"
                          }]
                        })}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        Add Plan
                      </button>
                    </div>
                    <div className="space-y-4">
                      {pricingSection.plans.map((plan, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900">Plan {index + 1}</h4>
                            <button
                              onClick={() => setPricingSection({
                                ...pricingSection,
                                plans: pricingSection.plans.filter((_, i) => i !== index)
                              })}
                              className="text-red-600 hover:text-red-800 transition-colors p-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Plan Name</label>
                                <input
                                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  value={plan.name}
                                  onChange={(e) => setPricingSection({
                                    ...pricingSection,
                                    plans: pricingSection.plans.map((p, idx) =>
                                      idx === index ? { ...p, name: e.target.value } : p
                                    )
                                  })}
                                  placeholder="Plan name..."
                                />
                              </div>
                              
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Price</label>
                                <input
                                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  value={plan.price}
                                  onChange={(e) => setPricingSection({
                                    ...pricingSection,
                                    plans: pricingSection.plans.map((p, idx) =>
                                      idx === index ? { ...p, price: e.target.value } : p
                                    )
                                  })}
                                  placeholder="Price..."
                                />
                              </div>
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                              <textarea
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                value={plan.description}
                                onChange={(e) => setPricingSection({
                                  ...pricingSection,
                                  plans: pricingSection.plans.map((p, idx) =>
                                    idx === index ? { ...p, description: e.target.value } : p
                                  )
                                })}
                                placeholder="Plan description..."
                                rows={2}
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Features (one per line)</label>
                              <textarea
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                value={plan.features.join('\n')}
                                onChange={(e) => setPricingSection({
                                  ...pricingSection,
                                  plans: pricingSection.plans.map((p, idx) =>
                                    idx === index ? { ...p, features: e.target.value.split('\n').filter(f => f.trim()) } : p
                                  )
                                })}
                                placeholder="Feature 1&#10;Feature 2&#10;Feature 3..."
                                rows={3}
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Button Text</label>
                                <input
                                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  value={plan.buttonText}
                                  onChange={(e) => setPricingSection({
                                    ...pricingSection,
                                    plans: pricingSection.plans.map((p, idx) =>
                                      idx === index ? { ...p, buttonText: e.target.value } : p
                                    )
                                  })}
                                  placeholder="Button text..."
                                />
                              </div>
                              
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Popular Badge</label>
                                <input
                                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  value={plan.popularBadge}
                                  onChange={(e) => setPricingSection({
                                    ...pricingSection,
                                    plans: pricingSection.plans.map((p, idx) =>
                                      idx === index ? { ...p, popularBadge: e.target.value } : p
                                    )
                                  })}
                                  placeholder="Popular badge text..."
                                />
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-3">
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={plan.isPopular}
                                  onChange={(e) => setPricingSection({
                                    ...pricingSection,
                                    plans: pricingSection.plans.map((p, idx) =>
                                      idx === index ? { ...p, isPopular: e.target.checked } : p
                                    )
                                  })}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="ml-2 text-sm text-gray-700">Mark as Popular</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Preview */}
            <div className="bg-white rounded-xl shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Section Preview</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <h2 className="text-lg font-bold text-gray-900">{pricingSection.heading || "Heading"}</h2>
                  <p className="text-sm text-gray-600">{pricingSection.subtitle || "Subtitle"}</p>
                  
                  <div className="space-y-3">
                    {pricingSection.plans.slice(0, 2).map((plan, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-sm text-gray-900">{plan.name}</h4>
                          {plan.isPopular && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              {plan.popularBadge}
                            </span>
                          )}
                        </div>
                        <p className="text-lg font-bold text-gray-900 mb-1">{plan.price}</p>
                        <p className="text-xs text-gray-600 mb-2">{plan.description}</p>
                        <ul className="text-xs text-gray-600 space-y-1 mb-3">
                          {plan.features.slice(0, 2).map((feature, idx) => (
                            <li key={idx} className="flex items-center">
                              <span className="text-green-500 mr-1">✓</span>
                              {feature}
                            </li>
                          ))}
                        </ul>
                        <button className="w-full bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium">
                          {plan.buttonText}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'waiting' ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Waiting Section Editor */}
            <div className="xl:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">What Are You Waiting For Section</h2>
                  <p className="text-sm text-gray-600 mt-1">Edit the call-to-action section at the end of your landing page</p>
                </div>
                <div className="p-6 space-y-6">
                  {/* Headline */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Headline</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={waitingSection.headline}
                      onChange={e => setWaitingSection({ ...waitingSection, headline: e.target.value })}
                      placeholder="Enter the headline..."
                    />
                  </div>
                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                      value={waitingSection.description}
                      onChange={e => setWaitingSection({ ...waitingSection, description: e.target.value })}
                      placeholder="Enter the description..."
                      rows={3}
                    />
                  </div>
                  {/* CTA Text */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CTA Button Text</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={waitingSection.ctaText}
                      onChange={e => setWaitingSection({ ...waitingSection, ctaText: e.target.value })}
                      placeholder="Enter the CTA button text..."
                    />
                  </div>
                  {/* Rating Label */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rating Label</label>
                    <input
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      value={waitingSection.ratingLabel}
                      onChange={e => setWaitingSection({ ...waitingSection, ratingLabel: e.target.value })}
                      placeholder="Enter the rating label..."
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* Preview */}
            <div className="bg-white rounded-xl shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Section Preview</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <h2 className="text-lg font-bold text-gray-900">{waitingSection.headline || 'WHAT ARE YOU WAITING FOR?'}</h2>
                  <p className="text-sm text-gray-600">{waitingSection.description || 'Description'}</p>
                  <button className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                    {waitingSection.ctaText || 'CTA Button'}
                  </button>
                  <div className="flex items-center space-x-1 mt-2">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="text-yellow-400 text-lg">&#11088;</span>
                    ))}
                    <span className="ml-2 text-xs text-gray-600 font-semibold">{waitingSection.ratingLabel || 'Rating Label'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'footer' ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Footer Editor */}
            <div className="xl:col-span-2">
              <div className="bg-white rounded-xl shadow-sm border">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Footer Content</h2>
                  <p className="text-sm text-gray-600 mt-1">Edit the footer content and links</p>
                </div>
                
                <div className="p-6 space-y-8">
                  {/* Brand Section */}
                  <div>
                    <h3 className="text-md font-semibold text-gray-900 mb-4">Brand Information</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Logo Text</label>
                        <input
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          value={footer.brand.logoText}
                          onChange={(e) => setFooter({ ...footer, brand: { ...footer.brand, logoText: e.target.value } })}
                          placeholder="Logo text..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Disclaimer</label>
                        <textarea
                          className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                          value={footer.brand.disclaimer}
                          onChange={(e) => setFooter({ ...footer, brand: { ...footer.brand, disclaimer: e.target.value } })}
                          placeholder="Disclaimer text..."
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Social Links */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-md font-semibold text-gray-900">Social Media Links</h3>
                      <button
                        onClick={() => setFooter({
                          ...footer,
                          brand: {
                            ...footer.brand,
                            socialLinks: [...footer.brand.socialLinks, { platform: '', url: '', icon: '' }]
                          }
                        })}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        Add Social Link
                      </button>
                    </div>
                    <div className="space-y-4">
                      {footer.brand.socialLinks.map((link, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900">Social Link {index + 1}</h4>
                            <button
                              onClick={() => setFooter({
                                ...footer,
                                brand: {
                                  ...footer.brand,
                                  socialLinks: footer.brand.socialLinks.filter((_, i) => i !== index)
                                }
                              })}
                              className="text-red-600 hover:text-red-800 transition-colors p-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Platform</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={link.platform}
                                onChange={(e) => setFooter({
                                  ...footer,
                                  brand: {
                                    ...footer.brand,
                                    socialLinks: footer.brand.socialLinks.map((l, i) =>
                                      i === index ? { ...l, platform: e.target.value } : l
                                    )
                                  }
                                })}
                                placeholder="Platform name..."
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">URL</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={link.url}
                                onChange={(e) => setFooter({
                                  ...footer,
                                  brand: {
                                    ...footer.brand,
                                    socialLinks: footer.brand.socialLinks.map((l, i) =>
                                      i === index ? { ...l, url: e.target.value } : l
                                    )
                                  }
                                })}
                                placeholder="URL..."
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Icon URL</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={link.icon}
                                onChange={(e) => setFooter({
                                  ...footer,
                                  brand: {
                                    ...footer.brand,
                                    socialLinks: footer.brand.socialLinks.map((l, i) =>
                                      i === index ? { ...l, icon: e.target.value } : l
                                    )
                                  }
                                })}
                                placeholder="Icon URL..."
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Quick Links */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-md font-semibold text-gray-900">Quick Links</h3>
                      <button
                        onClick={() => setFooter({
                          ...footer,
                          quickLinks: [...footer.quickLinks, { text: '', url: '' }]
                        })}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        Add Quick Link
                      </button>
                    </div>
                    <div className="space-y-4">
                      {footer.quickLinks.map((link, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900">Quick Link {index + 1}</h4>
                            <button
                              onClick={() => setFooter({
                                ...footer,
                                quickLinks: footer.quickLinks.filter((_, i) => i !== index)
                              })}
                              className="text-red-600 hover:text-red-800 transition-colors p-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Text</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={link.text}
                                onChange={(e) => setFooter({
                                  ...footer,
                                  quickLinks: footer.quickLinks.map((l, i) =>
                                    i === index ? { ...l, text: e.target.value } : l
                                  )
                                })}
                                placeholder="Link text..."
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">URL</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={link.url}
                                onChange={(e) => setFooter({
                                  ...footer,
                                  quickLinks: footer.quickLinks.map((l, i) =>
                                    i === index ? { ...l, url: e.target.value } : l
                                  )
                                })}
                                placeholder="URL..."
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Customer Service */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-md font-semibold text-gray-900">Customer Service</h3>
                      <button
                        onClick={() => setFooter({
                          ...footer,
                          customerService: [...footer.customerService, { text: '', url: '' }]
                        })}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        Add Service Link
                      </button>
                    </div>
                    <div className="space-y-4">
                      {footer.customerService.map((link, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900">Service Link {index + 1}</h4>
                            <button
                              onClick={() => setFooter({
                                ...footer,
                                customerService: footer.customerService.filter((_, i) => i !== index)
                              })}
                              className="text-red-600 hover:text-red-800 transition-colors p-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Text</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={link.text}
                                onChange={(e) => setFooter({
                                  ...footer,
                                  customerService: footer.customerService.map((l, i) =>
                                    i === index ? { ...l, text: e.target.value } : l
                                  )
                                })}
                                placeholder="Link text..."
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">URL</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={link.url}
                                onChange={(e) => setFooter({
                                  ...footer,
                                  customerService: footer.customerService.map((l, i) =>
                                    i === index ? { ...l, url: e.target.value } : l
                                  )
                                })}
                                placeholder="URL..."
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Payment Methods */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-md font-semibold text-gray-900">Payment Methods</h3>
                      <button
                        onClick={() => setFooter({
                          ...footer,
                          paymentMethods: [...footer.paymentMethods, { name: '', icon: '' }]
                        })}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      >
                        Add Payment Method
                      </button>
                    </div>
                    <div className="space-y-4">
                      {footer.paymentMethods.map((payment, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900">Payment Method {index + 1}</h4>
                            <button
                              onClick={() => setFooter({
                                ...footer,
                                paymentMethods: footer.paymentMethods.filter((_, i) => i !== index)
                              })}
                              className="text-red-600 hover:text-red-800 transition-colors p-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={payment.name}
                                onChange={(e) => setFooter({
                                  ...footer,
                                  paymentMethods: footer.paymentMethods.map((p, i) =>
                                    i === index ? { ...p, name: e.target.value } : p
                                  )
                                })}
                                placeholder="Payment name..."
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Icon URL</label>
                              <input
                                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                value={payment.icon}
                                onChange={(e) => setFooter({
                                  ...footer,
                                  paymentMethods: footer.paymentMethods.map((p, i) =>
                                    i === index ? { ...p, icon: e.target.value } : p
                                  )
                                })}
                                placeholder="Icon URL..."
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="bg-white rounded-xl shadow-sm border">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Footer Preview</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {/* Brand */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">{footer.brand.logoText}</h4>
                    <p className="text-xs text-gray-600 mb-2">{footer.brand.disclaimer}</p>
                    <div className="flex space-x-2">
                      {footer.brand.socialLinks.map((link, index) => (
                        <a key={index} href={link.url} className="text-blue-600 hover:text-blue-800">
                          <img src={link.icon} alt={link.platform} className="w-6 h-6" />
                        </a>
                      ))}
                    </div>
                  </div>

                  {/* Quick Links */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Quick Links</h4>
                    <div className="space-y-1">
                      {footer.quickLinks.map((link, index) => (
                        <a key={index} href={link.url} className="block text-xs text-gray-600 hover:text-gray-800">
                          {link.text}
                        </a>
                      ))}
                    </div>
                  </div>

                  {/* Customer Service */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Customer Service</h4>
                    <div className="space-y-1">
                      {footer.customerService.map((link, index) => (
                        <a key={index} href={link.url} className="block text-xs text-gray-600 hover:text-gray-800">
                          {link.text}
                        </a>
                      ))}
                    </div>
                  </div>

                  {/* Payment Methods */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Payment Methods</h4>
                    <div className="flex space-x-2">
                      {footer.paymentMethods.map((payment, index) => (
                        <img key={index} src={payment.icon} alt={payment.name} className="w-6 h-6" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Product</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{productSection.products[deleteConfirmIndex]?.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmIndex(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}